import type { Route } from 'nextjs-routes';

import config from 'configs/app';
import { INDEXABLE_STATIC_ROUTES } from 'lib/seo/indexableRoutes';

type QueryValue = string | Array<string> | undefined;
type QueryRecord = Record<string, QueryValue>;
type CanonicalBuilder = (query: QueryRecord) => string | undefined;

function getQueryString(value: QueryValue) {
  return typeof value === 'string' ? value : undefined;
}

function buildUrl(path: string) {
  return `${ config.app.baseUrl }${ path }`;
}

function fromParams(
  query: QueryRecord,
  paramNames: Array<string>,
  buildPath: (...values: Array<string>) => string,
) {
  const values = paramNames.map((name) => getQueryString(query[name]));

  if (values.every(Boolean)) {
    return buildUrl(buildPath(...(values as Array<string>)));
  }

  return undefined;
}

const DYNAMIC_CANONICAL_BUILDERS = new Map<Route['pathname'], CanonicalBuilder>([
  [ '/tx/[hash]', (query) => fromParams(query, [ 'hash' ], (hash) => `/tx/${ hash }`) ],
  [ '/op/[hash]', (query) => fromParams(query, [ 'hash' ], (hash) => `/op/${ hash }`) ],
  [ '/address/[hash]', (query) => fromParams(query, [ 'hash' ], (hash) => `/address/${ hash }`) ],
  [ '/block/[height_or_hash]', (query) => fromParams(query, [ 'height_or_hash' ], (value) => `/block/${ value }`) ],
  [ '/token/[hash]', (query) => fromParams(query, [ 'hash' ], (hash) => `/token/${ hash }`) ],
  [ '/token/[hash]/instance/[id]', (query) => fromParams(query, [ 'hash', 'id' ], (hash, id) => `/token/${ hash }/instance/${ id }`) ],
  [ '/stats/[id]', (query) => fromParams(query, [ 'id' ], (id) => `/stats/${ id }`) ],
  [ '/batches/[number]', (query) => fromParams(query, [ 'number' ], (number) => `/batches/${ number }`) ],
  [ '/pools/[hash]', (query) => fromParams(query, [ 'hash' ], (hash) => `/pools/${ hash }`) ],
  [ '/blobs/[hash]', (query) => fromParams(query, [ 'hash' ], (hash) => `/blobs/${ hash }`) ],
  [ '/name-domains/[name]', (query) => fromParams(query, [ 'name' ], (name) => `/name-domains/${ encodeURIComponent(name) }`) ],
  [ '/apps/[id]', (query) => fromParams(query, [ 'id' ], (id) => `/apps/${ id }`) ],
]);

export default function getCanonicalUrl(
  pathname: Route['pathname'],
  query: QueryRecord = {},
) {
  if (INDEXABLE_STATIC_ROUTES.includes(pathname)) {
    return pathname === '/' ? config.app.baseUrl : buildUrl(pathname);
  }

  return DYNAMIC_CANONICAL_BUILDERS.get(pathname)?.(query);
}
